import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";

const FOCAVEIS = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Diálogo acessível: Esc fecha, foco preso dentro, rolagem da página travada,
 * foco volta para quem abriu. No celular abre como folha de baixo para cima.
 * Sem "titulo", use "rotulo" para o leitor de tela anunciar o diálogo (padrão: "Detalhes").
 */
const Dialogo = ({ aberto, aoFechar, titulo, rotulo = "Detalhes", rodape, className = "", children }) => {
  const painelRef = useRef(null);
  const tituloId = useId();
  const aoFecharRef = useRef(aoFechar);
  aoFecharRef.current = aoFechar;

  useEffect(() => {
    if (!aberto) return undefined;
    const anterior = document.activeElement;
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const painel = painelRef.current;
    const primeiro = painel?.querySelector(FOCAVEIS);
    (primeiro || painel)?.focus();

    const aoTeclar = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        aoFecharRef.current?.();
        return;
      }
      if (e.key !== "Tab" || !painel) return;
      const itens = Array.from(painel.querySelectorAll(FOCAVEIS));
      if (itens.length === 0) {
        e.preventDefault();
        return;
      }
      const inicio = itens[0];
      const fim = itens[itens.length - 1];
      if (e.shiftKey && document.activeElement === inicio) {
        e.preventDefault();
        fim.focus();
      } else if (!e.shiftKey && document.activeElement === fim) {
        e.preventDefault();
        inicio.focus();
      }
    };
    document.addEventListener("keydown", aoTeclar);

    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = overflowAnterior;
      if (anterior && typeof anterior.focus === "function") anterior.focus();
    };
  }, [aberto]);

  if (!aberto) return null;

  return createPortal(
    <div className="gm-dialogo-fundo" onMouseDown={(e) => e.target === e.currentTarget && aoFechar?.()}>
      <div
        ref={painelRef}
        className={`gm-dialogo ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titulo ? tituloId : undefined}
        aria-label={titulo ? undefined : rotulo}
        tabIndex={-1}
      >
        <button type="button" className="gm-dialogo__fechar" onClick={aoFechar} aria-label="Fechar">
          <FaTimes aria-hidden="true" />
        </button>
        {titulo && (
          <h2 id={tituloId} className="gm-dialogo__titulo">
            {titulo}
          </h2>
        )}
        <div className="gm-dialogo__corpo">{children}</div>
        {rodape && <div className="gm-dialogo__rodape">{rodape}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Dialogo;
